<template>
  <div
    v-if="isVisible"
    class="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/45 animate-fadeIn"
    @click="handleClose"
  >
    <div
      class="mx-auto w-full max-w-xs bg-white rounded-xl border border-gray-200 shadow-lg transition-all duration-200 animate-slideIn"
      @click.stop
    >
      <!-- 图标和标题 -->
      <div class="flex flex-col items-center pt-6 pb-4">
        <div
          class="flex justify-center items-center mb-3 w-12 h-12 bg-red-500 rounded-full border border-gray-200"
        >
          <AlertCircle :size="20" color="white" />
        </div>
        <h3
          class="text-lg font-semibold tracking-tight text-center text-gray-900"
        >
          {{ title }}
        </h3>
      </div>

      <!-- 内容 -->
      <div class="px-6 pb-6" v-if="message">
        <p class="text-sm leading-relaxed text-center text-gray-600">
          {{ message }}
        </p>
      </div>

      <!-- 内容 -->
      <div class="px-6 pb-5">
        <p class="text-xs leading-relaxed text-center text-gray-400 pb-3">
          错误已被捕获, 我们会尽快处理, 感谢您的理解~
        </p>
        <p
          v-if="showRefreshButton"
          class="text-xs leading-relaxed text-center text-gray-400"
        >
          您可以点击重刷按钮,尝试重新刷新;
        </p>
        <!-- <p
          class="text-xs leading-relaxed text-center text-gray-400"
          @click="handleFeedback"
        >
          您也可点击<span class="text-primary">这里</span
          >与开发同学直接狐聊反馈!
        </p> -->
      </div>

      <!-- 按钮 -->
      <div class="px-6 pb-6">
        <div class="flex items-center gap-3">
          <button
            v-if="showRefreshButton"
            class="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md shadow-sm hover:bg-gray-200"
            @click.stop="handleRefreshWrapper"
          >
            重刷{{ refreshCount ? '(' + refreshCount + ')' : '' }}
          </button>
          <button
            v-if="showRefreshButton"
            class="flex-1 px-4 py-2.5 font-medium text-white bg-red-600 rounded-lg shadow-sm transition-colors duration-200 hover:bg-red-700"
            @click="handleClose"
          >
            我知道了
          </button>
          <button
            v-else
            class="flex-1 px-4 py-2.5 font-medium text-white bg-red-600 rounded-lg shadow-sm transition-colors duration-200 hover:bg-red-700"
            @click="handleExitApp"
          >
            退出
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { AlertCircle } from 'lucide-vue-next'
// import { useUserStore } from '../../hooks/useUserStore'

interface Props {
  isVisible: boolean
  title: string
  message: string
}

interface Emits {
  (e: 'close'): void
  (e: 'update:isVisible', value: boolean): void
}

defineProps<Props>()
const emit = defineEmits<Emits>()

// const { userState } = useUserStore()

const handleClose = () => {
  // 重置重刷次数记录
  recoveryRefreshCount()

  emit('close')
  emit('update:isVisible', false)
}

const handleExitApp = () => {
  handleClose()
  window.hxe.exit()
}

// const handleFeedback = () => {
//   window.hxe.joinGroup({
//     empIds: [userState.email],
//     groupId: import.meta.env.VITE_FEEDBACK_ROOM_ID,
//     success() {
//       window.hxe.showChat({
//         group: true,
//         target: import.meta.env.VITE_FEEDBACK_ROOM_ID,
//         success() {},
//         fail() {
//           window.hxe.showToast({
//             content: '不好意思, 狐聊拉起失败!'
//           })
//         }
//       })
//     },
//     fail(e) {
//       if (e.code === 4) {
//         window.hxe.showChat({
//           group: true,
//           target: import.meta.env.VITE_FEEDBACK_ROOM_ID,
//           success() {},
//           fail() {
//             window.hxe.showToast({
//               content: '不好意思, 狐聊拉起失败!'
//             })
//           }
//         })
//       } else
//         window.hxe.showToast({
//           content: '不好意思, 拉起狐聊失败!'
//         })
//     }
//   })
// }

const handleRefresh = () => {
  window.location.reload()
}

// 重刷次数记录，保存在 localStorage 中，超过或等于 3 次则隐藏按钮
const STORAGE_KEY = 'error_toast_refresh_count'
const initialCount = (() => {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v ? parseInt(v, 10) || 0 : 0
  } catch (e) {
    return 0
  }
})()

const refreshCount = ref<number>(initialCount)
const showRefreshButton = computed(() => refreshCount.value < 2)

const incrementRefreshCount = () => {
  refreshCount.value++
  try {
    localStorage.setItem(STORAGE_KEY, String(refreshCount.value))
  } catch (e) {
    // ignore
  }
}

const recoveryRefreshCount = () => {
  refreshCount.value = 0
  try {
    localStorage.setItem(STORAGE_KEY, String(0))
  } catch (e) {
    // ignore
  }
}

// 包装 handleRefresh：先增加计数，再刷新
const handleRefreshWrapper = () => {
  incrementRefreshCount()
  handleRefresh()
}
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-out;
}

.animate-slideIn {
  animation: slideIn 0.2s ease-out;
}
</style>
